import React from "react";
import { X } from "lucide-react";

// Only formatting, not used on every keystroke
function formatInput(val) {
  // Remove non-digit/colon
  let cleaned = val.replace(/[^\d:]/g, "");
  let parts = cleaned.split(":");

  // Pure numbers (no colon)
  if (parts.length === 1) {
    let num = parts[0];
    if (!num) return "00:00";
    if (num.length <= 2) {
      // 0-99 minutes
      return `00:${num.padStart(2, "0")}:00`;
    } else if (num.length === 3) {
      return `0${num[0]}:${num.slice(1, 3).padStart(2, "0")}:00`;
    } else if (num.length === 4) {
      return `${num.slice(0, 2).padStart(2, "0")}:${num.slice(2, 4).padStart(2, "0")}:00`;
    } else if (num.length === 5 || num.length === 6) {
      return `${num.slice(0, 2).padStart(2, "0")}:${num.slice(2, 4).padStart(2, "0")}:${num.slice(4, 6).padEnd(2, "0")}`;
    }
    return "00:00";
  }

  // Colon input
  while (parts.length < 3) parts.unshift("00");
  parts = parts.slice(-3);
  return parts.map(p => p.padStart(2, "0")).join(":");
}

function EditableLabel({ value, onChange, defaultLabel, color }) {
  const [editing, setEditing] = React.useState(false);
  const [input, setInput] = React.useState(value);

  React.useEffect(() => {
    setInput(value);
  }, [value]);

  function handleBlur() {
    setEditing(false);
    onChange(input.trim() === "" ? defaultLabel : input.trim());
  }

  return editing ? (
    <input
      type="text"
      className="font-semibold text-lg border-b border-gray-300 focus:border-blue-400 bg-transparent px-1 py-0.5 outline-none"
      value={input}
      autoFocus
      maxLength={15}
      style={{ color: color }}  // Set the color for input
      onChange={e => setInput(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === "Escape") handleBlur();
      }}
    />
  ) : (
    <span
      className="font-semibold text-zinc-600 dark:text-zinc-300 cursor-pointer hover:underline"
      style={{ color: color }} // Set the color for span
      onClick={() => setEditing(true)}
      title="Click to edit label"
    >
      {value}
    </span>
  );
}

export default function CustomTimerModal({
  show,
  onClose,
  timerPairs,
  setTimerPairs,
  loopTimers,
  setLoopTimers,
  theme,
  onStartSequence,
}) {
  const safeTheme = theme || {
    colors: {
      background: "#fff",
      text: "#222",
      logoBackground: "#eee",
      logoText: "#222",
      timerText: "#222",
    },
    fontFamily: "inherit",
  };

  function handleTimeChange(idx, field, value) {
    const updated = [...timerPairs];
    updated[idx][field] = value;
    setTimerPairs(updated);
  }

  function handleBlur(idx, field, value) {
    const formatted = formatInput(value);
    const updated = [...timerPairs];
    updated[idx][field] = formatted;
    setTimerPairs(updated);
  }

  function handleAdd() {
    if (timerPairs.length < 10) {
      setTimerPairs([
        ...timerPairs,
        { timer: "00:00", break: "00:00", timerLabel: "TIMER", breakLabel: "BREAK" }
      ]);
    }
  }

  function handleRemove(idx) {
    setTimerPairs(timerPairs.filter((_, i) => i !== idx));
  }

  function stop(e) { e.stopPropagation(); }

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-end bg-black bg-opacity-40"
      onClick={onClose}
    >
      <div
        onClick={stop}
        className="relative rounded-xl p-6 w-[420px] mr-8 mt-16 shadow-xl border border-gray-200 flex flex-col gap-7"
        style={{
          background: safeTheme.colors.background,
          color: safeTheme.colors.text,
          border: `2px solid ${safeTheme.colors.logoBackground}`,
          minHeight: "350px"
        }}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-xl font-bold tracking-tight" style={{ color: safeTheme.colors.logoText }}>
            Custom Timer Sequence
          </h2>
          <button
            className="p-1 rounded hover:bg-zinc-200 dark:hover:bg-zinc-800 transition"
            onClick={onClose}
            title="Close"
          >
            <X size={20} />
          </button>
        </div>
        {/* Timer/Break Pairs */}
        <div className="flex flex-col gap-6">
          {timerPairs.map((pair, idx) => (
            <div key={idx} className="mb-2 pb-2 border-b last:border-b-0">
              <div className="flex justify-end items-center mb-2">
                {timerPairs.length > 1 && (
                  <button
                    className="ml-2 text-zinc-500 hover:text-red-500"
                    onClick={() => handleRemove(idx)}
                    title="Remove"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
              {/* TIMER FIELD */}
              <input
                type="text"
                value={pair.timer}
                onChange={e => {
                  // Remove non-digits only
                  const onlyNumbers = e.target.value.replace(/[^\d]/g, "");
                  handleTimeChange(idx, "timer", onlyNumbers);
                }}
                onBlur={e => handleBlur(idx, "timer", e.target.value)}
                onFocus={e => e.target.value = ""}      
                className="text-4xl font-bold mb-2 px-2 py-1 rounded-lg text-center w-[220px] focus:ring-0 focus:outline-none border-none"
                style={{
                  backgroundColor: safeTheme.colors.background,
                  color: safeTheme.colors.timerText,
                  fontFamily: safeTheme.fontFamily
                }}
                maxLength={8}
                placeholder="00:00"                     
              />

              {/* Editable TIMER label */}  
              <EditableLabel
              value={pair.timerLabel}
              onChange={newLabel => {
                const updated = [...timerPairs];
                updated[idx].timerLabel = newLabel || "TIMER";
                setTimerPairs(updated);
              }}
              defaultLabel="TIMER"
              color={safeTheme.colors.text}
            />

              {/* BREAK FIELD */}
              <input
                type="text"
                value={pair.break}
                onChange={e => {
                  const onlyNumbers = e.target.value.replace(/[^\d]/g, "");
                  handleTimeChange(idx, "break", onlyNumbers);
                }}
                onBlur={e => handleBlur(idx, "break", e.target.value)}
                onFocus={e => e.target.value = ""}
                className="text-4xl font-bold mb-2 px-2 py-1 rounded-lg text-center w-[220px] focus:ring-0 focus:outline-none border-none"
                style={{
                  backgroundColor: safeTheme.colors.background,
                  color: safeTheme.colors.timerText,
                  fontFamily: safeTheme.fontFamily
                }}
                maxLength={8}
                placeholder="00:00"
              />

              {/* Editable BREAK label */}
              <EditableLabel
              value={pair.breakLabel}
              onChange={newLabel => {
                const updated = [...timerPairs];
                updated[idx].breakLabel = newLabel || "BREAK";
                setTimerPairs(updated);
              }}
              defaultLabel="BREAK"
              color={safeTheme.colors.text}
                />
            </div>
          ))}
        </div>
        {/* Add & Loop */}
        <div className="flex justify-between items-center mt-2">
          <button
            className="flex items-center gap-2 px-3 py-1"
            style={{
              background: safeTheme.colors.logoBackground,
              color: safeTheme.colors.logoText,
              borderRadius: "12px",
              fontWeight: 600,
              opacity: timerPairs.length >= 10 ? 0.5 : 1,
              cursor: timerPairs.length >= 10 ? "not-allowed" : "pointer",
            }}
            disabled={timerPairs.length >= 10}
            onClick={handleAdd}
          >
            +
            <span className="text-sm">Add</span>
          </button>
          <label className="flex items-center gap-2 font-semibold select-none cursor-pointer">
            <input
              type="checkbox"
              checked={loopTimers}
              onChange={e => setLoopTimers(e.target.checked)}
              className="accent-zinc-600 w-5 h-5"
            />
            Loop
            <button
              onClick={() => {
                onClose();
                onStartSequence();
              }}
              className="mt-4 px-4 py-2 rounded-lg font-bold"
              style={{
                background: safeTheme.colors.logoBackground,
                color: safeTheme.colors.logoText,
              }}
            >
              Start Sequence
            </button>
          </label>
        </div>
      </div>
    </div>
  );
}
